import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { useWebAppAuth } from "../utils/useWebAppAuth";
import { getPlaysoutAPI } from "../utils/playsoutApi";

import Head from "next/head";
import Image from "next/image";

import { Game, games } from "../data/games";
import { NotificationProvider } from "../components/notification-provider";

const PENDING_GAME_ID_KEY = 'pending-game-id';

export default function HomePage() {
  const router = useRouter();
  const { authenticated, login, setAppReady, isAppReady } = useWebAppAuth();
  const [hasPlaysoutSession, setHasPlaysoutSession] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return getPlaysoutAPI().isLoggedIn();
  });

  const [recentGames, setRecentGames] = useState<Game[]>([]);
  const [popularGames, setPopularGames] = useState<Game[]>([]);

  // 设置app ready状态
  useEffect(() => {
    if (!isAppReady) {
      setAppReady();
    }
  }, [setAppReady, isAppReady]);

  // 从localStorage加载最近游戏
  useEffect(() => {
    // Popular games (all games)
    console.log('Games data:', games); // 调试信息
    if (games && Array.isArray(games) && games.length > 0) {
      loadRecentGames();
      setPopularGames(games);
    } else {
      console.error('Games data is invalid:', games);
      setPopularGames([]);
    }
  }, []);

  useEffect(() => {
    setHasPlaysoutSession(getPlaysoutAPI().isLoggedIn());
  }, [authenticated]);

  const loadRecentGames = () => {
    if (!games || !Array.isArray(games) || games.length === 0) {
      console.error('Games data is invalid in loadRecentGames:', games);
      setRecentGames([]);
      return;
    }
    
    const recentGameIds = JSON.parse(localStorage.getItem('recent-games') || '[]');
    const recentGames = recentGameIds
      .map((id: string) => games.find(game => game.id === id))
      .filter(Boolean) as Game[]; // 过滤掉undefined值
    setRecentGames(recentGames);
  };

  // 添加游戏到最近游戏列表
  const addToRecentGames = useCallback((gameId: string) => {
    try {
      if (!games || !Array.isArray(games) || games.length === 0) {
        console.error('Games data is invalid in addToRecentGames:', games);
        return;
      }
      
      const recentGameIds = JSON.parse(localStorage.getItem('recent-games') || '[]');

      // 移除已存在的相同游戏ID（如果有的话）
      const filteredIds = recentGameIds.filter((id: string) => id !== gameId);

      // 将新游戏ID添加到开头，并保留全部历史
      const newRecentIds = [gameId, ...filteredIds];

      // 保存到localStorage
      localStorage.setItem('recent-games', JSON.stringify(newRecentIds));

      // 更新状态
      const newRecentGames = newRecentIds
        .map(id => games.find(game => game.id === id))
        .filter(Boolean) as Game[];
      setRecentGames(newRecentGames);
    } catch (error) {
      console.error('Error saving recent game:', error);
    }
  }, []);

  // 登录成功后自动跳转到登录前点击的游戏
  useEffect(() => {
    if (!authenticated) return;

    const pendingGameId = sessionStorage.getItem(PENDING_GAME_ID_KEY);
    if (!pendingGameId) return;

    sessionStorage.removeItem(PENDING_GAME_ID_KEY);
    addToRecentGames(pendingGameId);
    router.push(`/games/${pendingGameId}`);
  }, [authenticated, router, addToRecentGames]);

  const handleGameClick = (game: Game) => {
    if (!game || !game.id) {
      console.error('Invalid game object in handleGameClick:', game);
      return;
    }
    
    if (!authenticated) {
      sessionStorage.setItem(PENDING_GAME_ID_KEY, game.id);
      login();
      return;
    }

    // 添加到最近游戏列表
    setTimeout(() => {
      addToRecentGames(game.id);
    }, 500);
    
    router.push(`/games/${game.id}`);
  };

  const handleAuthAction = () => {
    if (authenticated) {
      router.push('/profile');
    } else {
      login();
    }
  };

  const handleMeClick = () => {
    if (authenticated) {
      router.push('/profile');
    } else {
      login();
    }
  };

  return (
    <NotificationProvider>
      <Head>
        <title>Game Platform - Discover Amazing Games</title>
        <meta name="description" content="Explore the hottest casual games" />
      </Head>

      <main className="min-h-screen bg-game-bg">
        <div className="mobile-container">
          {/* Header */}
          <header className="flex items-center justify-between py-4 px-2">
            <div className="flex items-center">
              <div className="w-[40px] h-8 relative bg-transparent">
                <Image
                  src="/images/logo-white.png"
                  alt="Logo"
                  fill
                  className="object-contain"
                  priority
                  sizes="40px"
                  placeholder="empty"
                />
              </div>
            </div>
            <button
              onClick={handleAuthAction}
              className="flex items-center gap-2 bg-game-accent hover:bg-blue-600 text-white px-4 py-1 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {(authenticated || hasPlaysoutSession) ? 'Profile' : 'Login'}
            </button>
          </header>

          <div className="px-4 pb-20">
            {/* Recently Played - 只在有数据时显示 */}
            {recentGames && recentGames.length > 0 && (
              <section className="mb-8">
                <h2 className="text-game-text text-lg font-semibold mb-4">Recently Played</h2>
                <div className="grid grid-cols-2 gap-2">
                  {recentGames.map((game) => (
                    game && (
                      <GameCard
                        key={game.id}
                        game={game}
                        onClick={() => handleGameClick(game)}
                        showCategory={false}
                      />
                    )
                  ))}
                </div>
              </section>
            )}

            {/* Popular Games */}
            <section>
              <h2 className="text-game-text text-lg font-semibold mb-4">Popular Games</h2>
              <div className="grid grid-cols-2 gap-2">
                {popularGames && popularGames.length > 0 && popularGames.map((game) => (
                  game && (
                    <GameCard
                      key={game.id}
                      game={game}
                      onClick={() => handleGameClick(game)}
                    />
                  )
                ))}
              </div>
            </section>
          </div>

          {/* Bottom Navigation */}
          <nav className="bottom-nav fixed bottom-0 left-0 right-0 bg-game-bg-secondary border-t border-game-card">
            <div className="flex justify-around items-center h-16">
              <button className="flex flex-col items-center gap-1 text-game-accent">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                </svg>
                <span className="text-xs">Home</span>
              </button>
              <button 
                onClick={handleMeClick}
                className="flex flex-col items-center gap-1 text-game-text-secondary hover:text-game-accent transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-xs">Profile</span>
              </button>
            </div>
          </nav>
        </div>
      </main>
    </NotificationProvider>
  );
}

interface GameCardProps {
  game: Game;
  onClick: () => void;
  showCategory?: boolean;
}

function GameCard({ game, onClick, showCategory = true }: GameCardProps) {
  // 安全检查
  if (!game || !game.id || !game.title || !game.image) {
    console.error('Invalid game object in GameCard:', game);
    return null;
  }
  
  return (
    <div onClick={onClick} className="game-card">
      <div className="game-image">
        <Image
          src={game.image}
          alt={game.title}
          fill
          className="object-cover select-none"
          sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
          draggable={false}
        />
      </div>

      <div className="game-info">
        <div className="title-row">
          <h3 className="game-title">{game.title}</h3>
          {game.isNew && (
            <div className="badge new">
              NEW
            </div>
          )}
          {game.isHot && (
            <div className="badge hot">
              HOT
            </div>
          )}
        </div>
        {showCategory && game.category && (
          <>
            <div className="divider"></div>
            <p className="game-category">{game.category}</p>
          </>
        )}
      </div>
    </div>
  );
}
